import { Button, TextInput } from '@mantine/core';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VerifiedEmailRow } from '@/api';
import {
  type FlowStep,
  useConnectedAccountsFlow,
} from '@/components/ConnectedAccounts/useConnectedAccountsFlow';
import { openConfirmDelete } from '@/components/Ui/ConfirmDelete';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { Tag } from '@/components/Ui/Tag';
import { useUndoToast } from '@/components/Ui/Toast';
import { shortDate } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import { errorCode, errorMessage } from '../format';

const POLL_MS = 5000;
const PAGE = 50;

type Show = 'all' | 'pending' | 'unused';

/** Forms whose only recipient is this address, by title; null when that cannot be worked out. */
async function formsLeftWithoutRecipients(address: VerifiedEmailRow): Promise<string[] | null> {
  if (address.form_count === 0) return [];
  const state = useRustyState.getState();
  const summary = await state.refreshFormsSummary();
  if (!summary) return null;
  const candidates = summary.filter((f) => f.recipients_verified + f.recipients_pending === 1);
  const results = await Promise.all(
    candidates.map(async (f) => {
      try {
        const res = await state.api.getFormRecipients(f.id);
        const rows = res.data ?? [];
        return rows.length === 1 && rows[0].id === address.id ? f.title : null;
      } catch {
        return null;
      }
    })
  );
  return results.filter((x): x is string => x !== null);
}

export function AddressesScreen() {
  const { t, i18n } = useTranslation();
  const toast = useUndoToast();
  const api = useRustyState.getState().api;

  const [rows, setRows] = useState<VerifiedEmailRow[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [email, setEmail] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<Show>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; text: string } | null>(null);
  const [rowInfo, setRowInfo] = useState<{ id: string; text: string } | null>(null);
  const [nostrSign, setNostrSign] = useState<{
    id: string;
    sign: () => Promise<FlowStep>;
  } | null>(null);
  const flow = useConnectedAccountsFlow(api);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async (background = false) => {
    try {
      const res = await useRustyState
        .getState()
        .api.getVerifiedEmails(background ? { onError: false } : undefined);
      setRows(res.data ?? []);
      // The sidebar shows the account email from the store.
      useRustyState.setState({ verifiedEmails: res.data ?? [] });
      setLoadFailed(false);
    } catch {
      if (!background) setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const anyPending = (rows ?? []).some((r) => !r.is_verified);
  // The verification link opens outside the app, so poll until every address is confirmed.
  useEffect(() => {
    if (!anyPending) return;
    const id = window.setInterval(() => void load(true), POLL_MS);
    return () => window.clearInterval(id);
  }, [anyPending, load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    // The backend answers an address already on this account without sending anything.
    const existing = rows?.find((r) => r.email.toLowerCase() === value.toLowerCase());
    if (existing) {
      setEmail('');
      if (existing.is_verified) setAddError(t('addresses.add.already', { email: existing.email }));
      else await resend(existing);
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await api.newVerifiedEmail({ email: value }, { onSuccess: false, onError: false });
      setEmail('');
      toast(t('addresses.add.sent', { email: value }));
      await load();
    } catch (err) {
      const code = errorCode(err);
      if (code === 'email_owned_by_other_account') setAddError(t('addresses.add.otherAccount'));
      else if (code === 'resend_limit') setAddError(t('addresses.add.resendLimit'));
      else setAddError(errorMessage(err) ?? t('addresses.add.failed'));
    } finally {
      setAdding(false);
    }
  };

  const accountEmailError = (code: string | undefined) =>
    t(`connectedAccounts.error.${code}`, { defaultValue: t('addresses.accountEmailFailed') });

  const handleAccountEmailStep = async (row: VerifiedEmailRow, step: FlowStep) => {
    switch (step.kind) {
      case 'email_pending':
        setRowInfo({
          id: row.id,
          text: t('addresses.accountEmailCheckEmail', { email: row.email }),
        });
        return;
      case 'nostr_sign_required':
        setNostrSign({ id: row.id, sign: step.sign });
        return;
      case 'completed':
        toast(t('addresses.madeAccountEmail', { email: row.email }));
        await load();
        return;
      case 'error':
        setRowError({ id: row.id, text: accountEmailError(step.code) });
        return;
      default:
        return;
    }
  };

  const makeAccountEmail = async (row: VerifiedEmailRow) => {
    setBusy(row.id);
    setRowError(null);
    setRowInfo(null);
    setNostrSign(null);
    try {
      await handleAccountEmailStep(row, await flow.startAccountEmail(row.id));
    } catch (err) {
      setRowError({ id: row.id, text: accountEmailError(errorCode(err)) });
    } finally {
      setBusy(null);
    }
  };

  const signNostr = async (row: VerifiedEmailRow) => {
    const pending = nostrSign;
    if (!pending || pending.id !== row.id) return;
    setBusy(row.id);
    setNostrSign(null);
    try {
      await handleAccountEmailStep(row, await pending.sign());
    } catch (err) {
      setRowError({ id: row.id, text: accountEmailError(errorCode(err)) });
    } finally {
      setBusy(null);
    }
  };

  const resend = async (row: VerifiedEmailRow) => {
    setBusy(row.id);
    setRowError(null);
    try {
      await api.verifyVerifiedEmail(row.id, { onSuccess: false, onError: false });
      toast(t('addresses.resent', { email: row.email }));
      await load();
    } catch (err) {
      setRowError({
        id: row.id,
        text:
          errorCode(err) === 'resend_limit'
            ? t('addresses.add.resendLimit')
            : (errorMessage(err) ?? t('addresses.resendFailed')),
      });
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row: VerifiedEmailRow) => {
    setBusy(row.id);
    const orphans = await formsLeftWithoutRecipients(row);
    setBusy(null);
    openConfirmDelete({
      label: t('addresses.remove.title'),
      confirmLabel: t('addresses.remove.confirm'),
      description: (
        <div className={shared.stack}>
          <span>
            {row.form_count > 0
              ? t('addresses.remove.fromForms', { email: row.email, count: row.form_count })
              : t('addresses.remove.unused', { email: row.email })}
          </span>
          {orphans && orphans.length > 0 && (
            <span className={shared.riskText}>
              {t('addresses.remove.orphans', { count: orphans.length, titles: orphans.join(', ') })}
            </span>
          )}
          {orphans === null && row.form_count > 0 && (
            <span className={shared.help}>{t('addresses.remove.maybeOrphans')}</span>
          )}
          {(orphans === null ? row.form_count > 0 : orphans.length > 0) && (
            <span className={shared.help}>{t('addresses.remove.autoReplyOff')}</span>
          )}
        </div>
      ),
      onConfirm: async () => {
        const res = await api.deleteVerifiedEmail(row.id);
        const left = orphans?.length ?? 0;
        const state = useRustyState.getState();
        const autoOff = (res.auto_response_disabled ?? []).map(
          (id) => state.formsSummary?.find((f) => f.id === id)?.title ?? id
        );
        const done =
          left > 0
            ? t('addresses.remove.doneOrphans', { email: row.email, count: left })
            : t('addresses.remove.done', { email: row.email });
        toast(
          autoOff.length > 0
            ? `${done} ${t('addresses.remove.autoReplyOffDone', { titles: autoOff.join(', ') })}`
            : done
        );
        void state.refreshFormsSummary();
        void state.refreshProblemsCount();
        await load();
      },
    });
  };

  const all = rows ?? [];
  const pendingCount = all.filter((r) => !r.is_verified).length;
  const unusedCount = all.filter((r) => r.form_count === 0).length;
  const q = query.trim().toLowerCase();
  const filtered = all
    .filter((r) =>
      show === 'pending' ? !r.is_verified : show === 'unused' ? r.form_count === 0 : true
    )
    .filter((r) => !q || r.email.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        Number(b.is_account_email) - Number(a.is_account_email) || a.email.localeCompare(b.email)
    );
  const visible = showAll ? filtered : filtered.slice(0, PAGE);

  const status = (r: VerifiedEmailRow) => {
    if (!r.is_verified) {
      return (
        <span className={shared.stack} style={{ gap: 4 }}>
          <span>
            <Tag variant="pending">{t('ui.pendingVerification')}</Tag>
          </span>
          <span className={shared.help}>
            {t('addresses.linkSent', { day: shortDate(r.last_requested_at, i18n.language) })}
          </span>
        </span>
      );
    }
    return (
      <span className={shared.stack} style={{ gap: 4 }}>
        <span className={shared.row} style={{ gap: 6 }}>
          <Tag variant="ok">{t('ui.verified')}</Tag>
          {r.is_account_email && <Tag>{t('ui.accountEmail')}</Tag>}
        </span>
        {r.is_account_email && (
          <span className={shared.help}>{t('addresses.accountEmailNote')}</span>
        )}
      </span>
    );
  };

  const usedOn = (r: VerifiedEmailRow) => {
    if (r.form_count === undefined) return <span className={shared.sub}>–</span>;
    if (r.form_count === 0) return <span className={shared.sub}>{t('addresses.noForms')}</span>;
    const titles = (r.form_titles ?? []).slice(0, 3);
    const more = r.form_count - titles.length;
    return (
      <span>
        {t('addresses.formCount', { count: r.form_count })}
        {titles.length > 0 && (
          <span className={shared.help}>
            {' · '}
            {more > 0
              ? t('addresses.titlesAndMore', { titles: titles.join(', '), count: more })
              : titles.join(', ')}
          </span>
        )}
      </span>
    );
  };

  const actions = (r: VerifiedEmailRow) => {
    if (r.is_account_email) {
      return <span className={shared.help}>{t('addresses.cannotRemove')}</span>;
    }
    return (
      <span className={shared.stack} style={{ gap: 4 }}>
        <span className={shared.nowrap}>
          {r.is_verified && nostrSign?.id === r.id ? (
            <Button
              variant="subtle"
              size="xs"
              onClick={() => signNostr(r)}
              disabled={busy !== null}
            >
              {t('security.methods.nostrSignButton')}
            </Button>
          ) : r.is_verified ? (
            <Button
              variant="subtle"
              size="xs"
              onClick={() => makeAccountEmail(r)}
              loading={busy === r.id}
              disabled={busy !== null && busy !== r.id}
            >
              {t('addresses.makeAccountEmail')}
            </Button>
          ) : (
            <Button
              variant="subtle"
              size="xs"
              onClick={() => resend(r)}
              loading={busy === r.id}
              disabled={busy !== null && busy !== r.id}
            >
              {t('addresses.resend')}
            </Button>
          )}
          <Button
            variant="subtle"
            size="xs"
            onClick={() => remove(r)}
            disabled={busy !== null}
            aria-label={t('addresses.removeLabel', { email: r.email })}
          >
            {t('addresses.removeButton')}
          </Button>
        </span>
        {rowInfo?.id === r.id && (
          <span className={shared.help} role="status">
            {rowInfo.text}
          </span>
        )}
        {rowError?.id === r.id && <span className={shared.riskText}>{rowError.text}</span>}
      </span>
    );
  };

  return (
    <Page>
      <PageHeader title={t('addresses.pageTitle')} subtitle={t('addresses.subtitle')} />

      <form className={shared.panel} onSubmit={add} noValidate>
        <label htmlFor="address-add" style={{ fontWeight: 500 }}>
          {t('addresses.add.label')}
        </label>
        <div className={shared.row}>
          <TextInput
            id="address-add"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value);
              setAddError(null);
            }}
            error={Boolean(addError)}
            aria-invalid={Boolean(addError)}
            aria-describedby={addError ? 'address-add-error' : undefined}
            style={{ flex: '1 1 320px' }}
            styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
            disabled={adding}
          />
          <Button type="submit" variant="default" loading={adding} disabled={!email.trim()}>
            {t('addresses.add.button')}
          </Button>
        </div>
        {addError && (
          <p id="address-add-error" className={shared.errorText} role="alert">
            {addError}
          </p>
        )}
        <span className={shared.help}>{t('addresses.add.help')}</span>
      </form>

      <div className={shared.filters}>
        <label htmlFor="address-q" className={shared.visuallyHidden}>
          {t('addresses.search')}
        </label>
        <TextInput
          id="address-q"
          type="search"
          placeholder={t('addresses.searchPlaceholder', { count: all.length })}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          style={{ flex: '1 1 280px' }}
        />
        <label htmlFor="address-show" className={shared.visuallyHidden}>
          {t('addresses.show.label')}
        </label>
        <select
          id="address-show"
          className={shared.nativeInput}
          value={show}
          onChange={(e) => setShow(e.currentTarget.value as Show)}
        >
          <option value="all">{t('addresses.show.all')}</option>
          <option value="pending">{t('addresses.show.pending', { count: pendingCount })}</option>
          <option value="unused">{t('addresses.show.unused', { count: unusedCount })}</option>
        </select>
      </div>

      {loadFailed ? (
        <p className={shared.errorText}>{t('addresses.loadFailed')}</p>
      ) : rows === null ? (
        <EmptyText>{t('ui.loading')}</EmptyText>
      ) : (
        <div className={shared.tableCard}>
          <DataTable
            caption={t('addresses.pageTitle')}
            rows={visible}
            rowKey={(r) => r.id}
            empty={q || show !== 'all' ? t('addresses.noMatch') : t('addresses.empty')}
            columns={[
              {
                key: 'address',
                header: t('addresses.col.address'),
                render: (r) => <span className={shared.address}>{r.email}</span>,
              },
              { key: 'status', header: t('addresses.col.status'), render: status },
              { key: 'used', header: t('addresses.col.recipientOn'), render: usedOn },
              {
                key: 'actions',
                header: <span className={shared.visuallyHidden}>{t('addresses.col.actions')}</span>,
                render: actions,
              },
            ]}
          />
          {filtered.length > PAGE && !showAll && (
            <div className={shared.footer}>
              <span className={shared.help}>
                {t('addresses.shown', { shown: visible.length, total: filtered.length })}
              </span>
              <Button size="xs" variant="default" onClick={() => setShowAll(true)}>
                {t('addresses.showAll')}
              </Button>
            </div>
          )}
        </div>
      )}

      <p className={shared.help} style={{ margin: 0 }}>
        {t('addresses.removeNote')}
      </p>
    </Page>
  );
}
